(function () {
  "use strict";

  var PRICE_ENDPOINT = "/apps/bb-portal/portal/v1/cart/price";
  var PREPARE_ENDPOINT = "/apps/bb-portal/portal/v1/checkout/prepare";
  var IDEMPOTENCY_STORAGE_KEY = "bb-authoritative-checkout-idempotency";
  var state = {
    pricingFingerprint: "",
    idempotencyKey: "",
    idempotencyFingerprint: "",
    nativeBypass: false,
    pricingPromise: null,
    pricingRevision: -1,
    cartRevision: 0,
    redirecting: false,
    timer: null,
    deliveryRepairing: "",
    deliveryRepairFailures: new Set(),
    automaticBundleDeliveryUpdates: new Map(),
  };
  var settings = window.BBAuthoritativeCheckout || {};
  var messages = settings.messages || {};

  function message(key, fallback) {
    return messages[key] || fallback;
  }

  function notify(variant, title, detail) {
    if (window.BBToast && typeof window.BBToast.show === "function") {
      window.BBToast.show({
        variant: variant,
        title: title,
        message: detail || "",
        dedupeKey: "authoritative-checkout",
      });
    }
    var status = document.querySelector(
      "[data-bb-authoritative-checkout-status]",
    );
    if (!status) {
      status = document.createElement("p");
      status.hidden = true;
      status.dataset.bbAuthoritativeCheckoutStatus = "";
      status.setAttribute("role", variant === "error" ? "alert" : "status");
      status.setAttribute("aria-live", "polite");
      document.body.appendChild(status);
    }
    status.textContent = [title, detail].filter(Boolean).join(" ");
  }

  function checkoutButtons() {
    return Array.from(document.querySelectorAll('[name="checkout"]'));
  }

  function createSpinner() {
    var namespace = "http://www.w3.org/2000/svg";
    var spinner = document.createElementNS(namespace, "svg");
    var track = document.createElementNS(namespace, "circle");
    var indicator = document.createElementNS(namespace, "path");

    spinner.setAttribute("class", "bb-checkout-spinner");
    spinner.setAttribute("viewBox", "0 0 24 24");
    spinner.setAttribute("width", "20");
    spinner.setAttribute("height", "20");
    spinner.setAttribute("aria-hidden", "true");
    track.setAttribute("cx", "12");
    track.setAttribute("cy", "12");
    track.setAttribute("r", "9");
    track.setAttribute("fill", "none");
    track.setAttribute("stroke", "currentColor");
    track.setAttribute("stroke-width", "2.5");
    track.setAttribute("opacity", "0.35");
    indicator.setAttribute("d", "M12 3a9 9 0 0 1 9 9");
    indicator.setAttribute("fill", "none");
    indicator.setAttribute("stroke", "currentColor");
    indicator.setAttribute("stroke-width", "2.5");
    indicator.setAttribute("stroke-linecap", "round");

    if (
      !window.matchMedia ||
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      var animation = document.createElementNS(namespace, "animateTransform");
      animation.setAttribute("attributeName", "transform");
      animation.setAttribute("type", "rotate");
      animation.setAttribute("from", "0 12 12");
      animation.setAttribute("to", "360 12 12");
      animation.setAttribute("dur", "0.7s");
      animation.setAttribute("repeatCount", "indefinite");
      indicator.appendChild(animation);
    }

    spinner.append(track, indicator);
    return spinner;
  }

  function setBusy(busy) {
    checkoutButtons().forEach(function (button) {
      if (busy) {
        if (button.dataset.bbCheckoutLoading !== "true") {
          var spinner = createSpinner();
          var loadingLabel = document.createElement("span");

          button.dataset.bbCheckoutWasDisabled = String(button.disabled);
          button.dataset.bbCheckoutOriginalHtml = button.innerHTML;
          button.dataset.bbCheckoutLoading = "true";
          loadingLabel.className = "visually-hidden";
          loadingLabel.textContent = message("preparing", "Preparing checkout");
          button.replaceChildren(spinner, loadingLabel);
        }
        button.disabled = true;
        button.setAttribute("aria-busy", "true");
      } else {
        button.disabled =
          button.dataset.bbCheckoutWasDisabled === "true" ||
          button.dataset.bbPricingBlocked === "true";
        if (button.dataset.bbCheckoutLoading === "true") {
          button.innerHTML = button.dataset.bbCheckoutOriginalHtml || "";
        }
        button.removeAttribute("aria-busy");
        delete button.dataset.bbCheckoutWasDisabled;
        delete button.dataset.bbCheckoutOriginalHtml;
        delete button.dataset.bbCheckoutLoading;
      }
    });
  }

  function fetchJson(url, options) {
    var requestOptions = Object.assign({}, options || {});
    var controller = typeof AbortController === "function" ? new AbortController() : null;
    var timeout = Number(settings.requestTimeoutMs);
    if (!Number.isFinite(timeout) || timeout < 1) timeout = 12000;
    var timer = null;
    if (controller) {
      requestOptions.signal = controller.signal;
      timer = window.setTimeout(function () {
        controller.abort();
      }, timeout);
    }
    return fetch(url, requestOptions)
      .then(function (response) {
        return response
          .json()
          .catch(function () {
            return {};
          })
          .then(function (data) {
            return { response: response, data: data };
          });
      })
      .finally(function () {
        if (timer) window.clearTimeout(timer);
      });
  }

  function loadCart() {
    return fetchJson("/cart.js", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    }).then(function (result) {
      if (!result.response.ok) throw new Error("cart_unavailable");
      return result.data;
    });
  }

  function post(url, body) {
    return fetchJson(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
  }

  function uploadArtworkFile(file) {
    if (!file) return Promise.reject(new Error("Choose an artwork file."));
    return post("/apps/bb-portal/portal/v1/artwork-uploads/prepare", {
      original_name: file.name,
      media_type: file.type || "application/octet-stream",
      size_bytes: file.size,
    }).then(function (prepared) {
      if (!prepared.response.ok || !prepared.data.signedUrl) {
        throw new Error(
          prepared.data.error || "Artwork upload could not start.",
        );
      }
      return fetch(prepared.data.signedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      }).then(function (stored) {
        if (!stored.ok) throw new Error("Artwork upload failed.");
        return post(
          "/apps/bb-portal/portal/v1/artwork-uploads/" +
            encodeURIComponent(prepared.data.file_id) +
            "/commit",
          { upload_token: prepared.data.upload_token },
        ).then(function (committed) {
          if (!committed.response.ok || !committed.data.file_id) {
            throw new Error(
              committed.data.error || "Artwork did not pass validation.",
            );
          }
          return committed.data;
        });
      });
    });
  }

  window.BBArtworkUpload = Object.freeze({ upload: uploadArtworkFile });

  function money(amount, currency) {
    var value = Number(amount);
    if (!Number.isFinite(value)) return "";
    if (window.BadgeBuzzMoney) {
      return window.BadgeBuzzMoney.format(value, {
        locale: document.documentElement.lang || "en-US",
        currency: currency || "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
        style: "currency",
        currency: currency || "USD",
      }).format(value);
    } catch (error) {
      return "$" + value.toFixed(2);
    }
  }

  function escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === "function") {
      return window.CSS.escape(value);
    }
    return String(value).replace(/["\\]/g, "\\$&");
  }

  function paintVerifiedPrice(target, original, final, suffix) {
    var finalText = final + (suffix || "");
    if (!original || original === final) {
      target.textContent = finalText;
      return;
    }
    var originalPrice = document.createElement("s");
    var finalPrice = document.createElement("span");
    originalPrice.className = "bb-item-price__old cart-item__old-price";
    originalPrice.dataset.bbAuthoritativeOriginalPrice = "";
    originalPrice.textContent = original;
    finalPrice.className = "bb-item-price__new";
    finalPrice.dataset.bbAuthoritativeFinalPrice = "";
    finalPrice.textContent = finalText;
    target.replaceChildren(
      originalPrice,
      document.createTextNode(" "),
      finalPrice,
    );
  }

  function displayedLineQuantity(row) {
    var stored = Number(row.dataset.bbQuantity);
    if (Number.isSafeInteger(stored) && stored > 0) return stored;
    var target = row.querySelector("[data-bb-item-quantity]");
    var match = target && target.textContent.match(/\d+/);
    return match ? Number(match[0]) : null;
  }

  function bundleHeadings(bundleKey) {
    if (!bundleKey) return [];
    return Array.from(
      document.querySelectorAll(
        '[data-bb-bundle-heading="' + escapeSelector(bundleKey) + '"]',
      ),
    );
  }

  function bundleRows(bundleKey) {
    if (!bundleKey) return [];
    return Array.from(
      document.querySelectorAll(
        '[data-bb-bundle-key="' + escapeSelector(bundleKey) + '"]',
      ),
    );
  }

  function paintBundleSummary(bundleKey) {
    var rows = bundleRows(bundleKey);
    if (!rows.length) return;
    var quantities = rows
      .map(displayedLineQuantity)
      .filter(function (quantity) {
        return Number.isSafeInteger(quantity) && quantity > 0;
      });
    var dates = rows
      .map(function (row) {
        return row.dataset.bbDeliveryDate || "";
      })
      .filter(function (date) {
        return /^\d{4}-\d{2}-\d{2}$/.test(date);
      })
      .sort();
    var sharedQuantity = null;
    if (quantities.length === rows.length) {
      var distinct = new Set(quantities);
      if (distinct.size === 1) {
        sharedQuantity = quantities[0];
      }
    }
    var deliveryText = dates.length
      ? "Ready to ship by " + formatDeliveryDate(dates.at(-1))
      : "";
    bundleHeadings(bundleKey).forEach(function (heading) {
      var summary = heading.querySelector("[data-bb-bundle-summary]");
      if (heading.hasAttribute("data-bb-bundle-representative")) {
        if (summary && sharedQuantity != null) {
          summary.textContent =
            sharedQuantity + (sharedQuantity === 1 ? " bundle" : " bundles");
        }
        var delivery = heading.querySelector(
          "[data-bb-bundle-delivery-summary]",
        );
        if (delivery) {
          delivery.textContent = deliveryText;
          if (deliveryText) delivery.setAttribute("aria-label", deliveryText);
          else delivery.removeAttribute("aria-label");
        }
        return;
      }
      var parts = [
        rows.length + (rows.length === 1 ? " product" : " products"),
      ];
      if (sharedQuantity != null) {
        parts.push(
          sharedQuantity + (sharedQuantity === 1 ? " bundle" : " bundles"),
        );
      }
      if (deliveryText) parts.push(deliveryText);
      if (summary) summary.textContent = parts.join(" · ");
    });
    var automaticUpdate = state.automaticBundleDeliveryUpdates.get(bundleKey);
    if (automaticUpdate) {
      paintAutomaticBundleDeliveryUpdate(
        bundleKey,
        automaticUpdate.option,
        automaticUpdate.options,
      );
    }
  }

  function paintBundlePricing(lines) {
    var groups = new Map();
    (Array.isArray(lines) ? lines : []).forEach(function (line) {
      if (!line.bundle_key || line.line_total == null) return;
      var group = groups.get(line.bundle_key) || {
        currency: line.currency,
        original: 0,
        final: 0,
        valid: true,
      };
      var original = Number(line.original_line_total);
      var final = Number(line.line_total);
      if (
        !Number.isFinite(original) ||
        !Number.isFinite(final) ||
        group.currency !== line.currency
      ) {
        group.valid = false;
      } else {
        group.original += original;
        group.final += final;
      }
      groups.set(line.bundle_key, group);
    });
    groups.forEach(function (group, bundleKey) {
      if (!group.valid) return;
      var original = money(group.original, group.currency);
      var final = money(group.final, group.currency);
      bundleHeadings(bundleKey).forEach(function (heading) {
        var total = heading.querySelector("[data-bb-bundle-total]");
        var unitPrice = heading.querySelector("[data-bb-bundle-unit-price]");
        var status = heading.querySelector("[data-bb-bundle-status]");
        if (total)
          paintVerifiedPrice(
            total,
            original,
            final,
            heading.hasAttribute("data-bb-bundle-representative")
              ? " total"
              : "",
          );
        if (unitPrice) {
          var rows = bundleRows(bundleKey);
          var quantities = rows.map(displayedLineQuantity);
          var validQuantity =
            quantities.length === rows.length &&
            quantities.length > 0 &&
            quantities.every(function (quantity) {
              return quantity === quantities[0];
            })
              ? quantities[0]
              : null;
          unitPrice.textContent = validQuantity
            ? money(group.final / validQuantity, group.currency) + " each"
            : "Price unavailable";
        }
        if (status) {
          status.hidden = true;
          status.textContent = "";
        }
        heading.dataset.bbBundlePricingState = "verified";
      });
      paintBundleSummary(bundleKey);
    });
  }

  function paintBundleBlocked(bundleKey) {
    bundleHeadings(bundleKey).forEach(function (heading) {
      var total = heading.querySelector("[data-bb-bundle-total]");
      var unitPrice = heading.querySelector("[data-bb-bundle-unit-price]");
      var status = heading.querySelector("[data-bb-bundle-status]");
      if (total) total.textContent = "Price unavailable";
      if (unitPrice) unitPrice.textContent = "Price unavailable";
      if (status) {
        status.hidden = false;
        status.textContent = "Review the highlighted product options.";
      }
      heading.dataset.bbBundlePricingState = "blocked";
    });
    paintBundleSummary(bundleKey);
  }

  function paintLine(line) {
    if (
      (line.pricing_strategy !== "supplier_authoritative" &&
        !line.bundle_key) ||
      line.line_total == null
    ) {
      return;
    }
    var selector = '[data-bb-line-key="' + escapeSelector(line.key) + '"]';
    document.querySelectorAll(selector).forEach(function (row) {
      var lineFormatted = money(line.line_total, line.currency);
      var originalLineFormatted = money(
        line.original_line_total,
        line.currency,
      );
      var bundleDiscount = Number(line.bundle_discount);
      var hasBundleDiscount =
        Boolean(line.bundle_key) &&
        Number.isFinite(bundleDiscount) &&
        bundleDiscount > 0 &&
        originalLineFormatted &&
        originalLineFormatted !== lineFormatted;
      row
        .querySelectorAll(
          ".bb-cart__item-price, .cart-item__price-wrapper .price--end",
        )
        .forEach(function (target) {
          paintVerifiedPrice(
            target,
            hasBundleDiscount ? originalLineFormatted : "",
            lineFormatted,
          );
        });
      if (line.unit_price != null) {
        var unitFormatted = money(line.unit_price, line.currency);
        var quantity = displayedLineQuantity(row);
        var originalUnitFormatted = quantity
          ? money(Number(line.original_line_total) / quantity, line.currency)
          : "";
        row.querySelectorAll(".bb-item-price").forEach(function (target) {
          target.hidden = false;
          paintVerifiedPrice(
            target,
            hasBundleDiscount ? originalUnitFormatted : "",
            unitFormatted,
            " each",
          );
        });
      }
      row.querySelectorAll("[data-bb-item-total]").forEach(function (target) {
        paintVerifiedPrice(
          target,
          hasBundleDiscount ? originalLineFormatted : "",
          lineFormatted,
          " total",
        );
      });
      row.dataset.bbAuthoritativePrice = line.line_total;
    });
  }

  function setCheckoutBlocked(blocked) {
    checkoutButtons().forEach(function (button) {
      button.dataset.bbPricingBlocked = String(blocked);
      if (button.dataset.bbCheckoutLoading !== "true") {
        button.disabled = blocked;
      }
      button.setAttribute("aria-disabled", String(blocked));
    });
  }

  function formatDeliveryDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) {
      return String(value || "");
    }
    try {
      return new Intl.DateTimeFormat(document.documentElement.lang || "en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      }).format(new Date(value + "T00:00:00Z"));
    } catch (error) {
      return value;
    }
  }

  function paintDeliveryNotice(lineKey, detail, variant, currentDate) {
    if (!lineKey || !detail) return;
    var selector = '[data-bb-line-key="' + escapeSelector(lineKey) + '"]';
    document.querySelectorAll(selector).forEach(function (row) {
      if (variant !== "error") {
        if (currentDate) {
          row
            .querySelectorAll("[data-bb-delivery-summary]")
            .forEach(function (summary) {
              summary.textContent =
                "Ready to ship by " + formatDeliveryDate(currentDate);
            });
        }
        var deliveryValue = row.querySelector("[data-bb-delivery-value]");
        if (!deliveryValue) return;
        if (currentDate) {
          deliveryValue.textContent = formatDeliveryDate(currentDate) + " ";
        }
        var status = deliveryValue.querySelector("[data-bb-delivery-status]");
        if (!status) {
          status = document.createElement("span");
          status.dataset.bbDeliveryStatus = "";
          status.className = "bb-cart-delivery-status";
          status.setAttribute("role", "status");
          status.setAttribute("aria-live", "polite");
          deliveryValue.appendChild(status);
        }
        status.textContent = detail;
        return;
      }
      var details = row.querySelector(".cart-item__details") || row;
      var notice = details.querySelector("[data-bb-delivery-notice]");
      if (!notice) {
        notice = document.createElement("div");
        notice.dataset.bbDeliveryNotice = "";
        notice.setAttribute("role", variant === "error" ? "alert" : "status");
        notice.setAttribute("aria-live", "polite");
        notice.innerHTML =
          '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="5.5" width="17" height="15" rx="2"></rect><path d="M8 3.5v4M16 3.5v4M3.5 10h17M8 15l2.3 2.3L16 12"></path></svg><span></span>';
        details.appendChild(notice);
      }
      notice.className =
        "bb-cart-delivery-notice" +
        (variant === "error" ? " bb-cart-delivery-notice--error" : "");
      notice.setAttribute("role", variant === "error" ? "alert" : "status");
      notice.querySelector("span").textContent = detail;
    });
  }

  function sortedDeliveryOptions(options) {
    return (Array.isArray(options) ? options.slice() : []).sort(function (a, b) {
      return String(a && a.date ? a.date : "").localeCompare(
        String(b && b.date ? b.date : ""),
      );
    });
  }

  function paintAutomaticDeliveryUpdate(lineKey, option, options) {
    if (!lineKey || !option || !option.date) return;
    var selector = '[data-bb-line-key="' + escapeSelector(lineKey) + '"]';
    document.querySelectorAll(selector).forEach(function (row) {
      row
        .querySelectorAll("[data-bb-delivery-notice]")
        .forEach(function (notice) {
          notice.remove();
        });
      row
        .querySelectorAll("[data-bb-delivery-summary]")
        .forEach(function (summary) {
          var change = document.createElement("button");
          summary.textContent =
            "Delivery updated to " + formatDeliveryDate(option.date) + " ";
          summary.setAttribute(
            "aria-label",
            "Delivery updated to " + formatDeliveryDate(option.date),
          );
          change.type = "button";
          change.className = "bb-cart-delivery-change";
          change.textContent = "Change";
          change.setAttribute("aria-label", "Change ready-to-ship date");
          change.addEventListener("click", function () {
            summary.textContent =
              "Ready to ship by " + formatDeliveryDate(option.date);
            paintDeliveryChoice(
              {
                lineKey: lineKey,
                message: "Choose another ready-to-ship date.",
                deliveryOptions: options,
              },
              false,
            );
            var choice = row.querySelector(".bb-cart-delivery-choice");
            if (choice) choice.focus();
          });
          summary.appendChild(change);
        });
    });
  }

  function chooseReplacementDelivery(
    lineKey,
    option,
    select,
    options,
    automatic,
  ) {
    if (!option || !option.date || !option.configuration) return;
    var repairKey = "line:" + lineKey;
    var loading = document.createElement("span");
    var spinner = createSpinner();
    loading.className = "bb-cart-delivery-loading";
    loading.setAttribute("role", "status");
    loading.setAttribute("aria-live", "polite");
    loading.setAttribute("aria-label", "Updating ready-to-ship date");
    spinner.setAttribute("class", "bb-cart-delivery-loading-spinner");
    spinner.setAttribute("width", "16");
    spinner.setAttribute("height", "16");
    loading.append(spinner, document.createTextNode("Updating…"));
    select.disabled = true;
    select.setAttribute("aria-busy", "true");
    select.replaceWith(loading);
    loadCart()
      .then(function (cart) {
        var line = (cart.items || []).find(function (item) {
          return item.key === lineKey;
        });
        if (!line) throw new Error("delivery_update_line_missing");
        var previousDate = String(
          (line.properties || {})["Delivery date"] || "",
        );
        var properties = Object.assign({}, line.properties || {}, {
          "Delivery date": option.date,
          "_BadgeBuzz configuration": option.configuration,
        });
        if (option.offer) {
          properties["_BadgeBuzz offer"] = option.offer;
        } else {
          delete properties["_BadgeBuzz offer"];
        }
        if (automatic && previousDate && previousDate !== option.date) {
          properties["_BadgeBuzz previous delivery date"] = previousDate;
        } else {
          delete properties["_BadgeBuzz previous delivery date"];
        }
        return changeLineAndReconcile(line, properties);
      })
      .then(function (result) {
        var replacement = result.line;
        updateLineKeyInDom(lineKey, replacement.key);
        var activeKey = replacement.key;
        document
          .querySelectorAll(
            '[data-bb-line-key="' +
              escapeSelector(activeKey) +
              '"] [data-bb-delivery-summary]',
          )
          .forEach(function (summary) {
            summary.textContent = "Ready to ship by " + formatDeliveryDate(option.date);
          });
        if (automatic) {
          paintAutomaticDeliveryUpdate(activeKey, option, options);
        } else {
          document
            .querySelectorAll(
              '[data-bb-line-key="' + escapeSelector(activeKey) + '"]',
            )
            .forEach(function (row) {
              row
                .querySelectorAll("[data-bb-delivery-notice]")
                .forEach(function (notice) {
                  notice.remove();
                });
            });
        }
        state.deliveryRepairFailures.delete(repairKey);
        document.dispatchEvent(
          new CustomEvent("cart:updated", {
            detail: { source: "delivery-choice" },
          }),
        );
        return priceCart().catch(function () {
          schedulePricing();
        });
      })
      .catch(function () {
        if (automatic) state.deliveryRepairFailures.add(repairKey);
        if (loading.isConnected) loading.replaceWith(select);
        notify(
          "error",
          message("delivery_update_failed", "Ready-to-ship date was not changed"),
          message("reload_cart", "Reload the cart and try again."),
        );
      })
      .finally(function () {
        if (state.deliveryRepairing === repairKey) {
          state.deliveryRepairing = "";
        }
        select.disabled = false;
        select.removeAttribute("aria-busy");
      });
  }

  function paintDeliveryChoice(error, allowAutomatic) {
    if (!error.lineKey || !Array.isArray(error.deliveryOptions)) return false;
    var options = sortedDeliveryOptions(error.deliveryOptions);
    if (!options.length) return false;
    var repairKey = "line:" + error.lineKey;
    var automatic =
      allowAutomatic !== false &&
      !state.deliveryRepairing &&
      !state.deliveryRepairFailures.has(repairKey);
    var automaticStarted = false;
    var selector = '[data-bb-line-key="' + escapeSelector(error.lineKey) + '"]';
    document.querySelectorAll(selector).forEach(function (row) {
      var details = row.querySelector(".cart-item__details") || row;
      var notice = details.querySelector("[data-bb-delivery-notice]");
      if (!notice) {
        paintDeliveryNotice(error.lineKey, error.message, "error");
        notice = details.querySelector("[data-bb-delivery-notice]");
      }
      if (!notice) return;
      notice
        .querySelectorAll(".bb-cart-delivery-loading")
        .forEach(function (loading) {
          loading.remove();
        });
      notice
        .querySelectorAll(".bb-cart-delivery-choice")
        .forEach(function (existing) {
          existing.remove();
        });
      var select = document.createElement("select");
      select.className = "bb-cart-delivery-choice";
      select.setAttribute("aria-label", "Choose a new ready-to-ship date");
      var placeholder = document.createElement("option");
      placeholder.value = "";
      placeholder.textContent = "Choose a new date";
      placeholder.selected = true;
      placeholder.disabled = true;
      select.appendChild(placeholder);
      options.forEach(function (option, index) {
        var item = document.createElement("option");
        item.value = String(index);
        item.textContent =
          "Ready to ship by " +
          formatDeliveryDate(option.date) +
          " · " +
          money(option.total, option.currency);
        select.appendChild(item);
      });
      select.addEventListener("change", function () {
        chooseReplacementDelivery(
          error.lineKey,
          options[Number(select.value)],
          select,
          options,
          false,
        );
      });
      notice.appendChild(select);
      if (automatic && !automaticStarted) {
        automaticStarted = true;
        state.deliveryRepairing = repairKey;
        notice.className = "bb-cart-delivery-notice";
        notice.setAttribute("role", "status");
        notice.querySelector("span").textContent =
          "Updating to the earliest available ready-to-ship date.";
        chooseReplacementDelivery(
          error.lineKey,
          options[0],
          select,
          options,
          true,
        );
      }
    });
    return true;
  }

  function paintAutomaticBundleDeliveryUpdate(bundleKey, option, options) {
    if (!bundleKey || !option || !option.date) return;
    bundleHeadings(bundleKey).forEach(function (heading) {
      heading
        .querySelectorAll("[data-bb-delivery-notice]")
        .forEach(function (notice) {
          notice.remove();
        });
      heading
        .querySelectorAll("[data-bb-bundle-delivery-summary]")
        .forEach(function (summary) {
          var change = document.createElement("button");
          summary.textContent =
            "Delivery updated to " + formatDeliveryDate(option.date) + " ";
          summary.setAttribute(
            "aria-label",
            "Bundle delivery updated to " + formatDeliveryDate(option.date),
          );
          change.type = "button";
          change.className = "bb-cart-delivery-change";
          change.textContent = "Change";
          change.setAttribute("aria-label", "Change bundle ready-to-ship date");
          change.addEventListener("click", function () {
            state.automaticBundleDeliveryUpdates.delete(bundleKey);
            summary.textContent =
              "Ready to ship by " + formatDeliveryDate(option.date);
            paintBundleDeliveryChoice(
              {
                bundleKey: bundleKey,
                message: "Choose another bundle ready-to-ship date.",
                deliveryOptions: options,
              },
              false,
            );
            var choice = heading.querySelector(".bb-cart-delivery-choice");
            if (choice) choice.focus();
          });
          summary.appendChild(change);
        });
    });
  }

  function chooseReplacementBundleDelivery(
    bundleKey,
    option,
    select,
    options,
    automatic,
  ) {
    if (
      !bundleKey ||
      !option ||
      !Array.isArray(option.components) ||
      !option.components.length
    ) {
      return;
    }
    var repairKey = "bundle:" + bundleKey;
    var loading = document.createElement("span");
    var spinner = createSpinner();
    loading.className = "bb-cart-delivery-loading";
    loading.setAttribute("role", "status");
    loading.setAttribute("aria-live", "polite");
    loading.setAttribute("aria-label", "Updating bundle delivery");
    spinner.setAttribute("class", "bb-cart-delivery-loading-spinner");
    spinner.setAttribute("width", "16");
    spinner.setAttribute("height", "16");
    loading.append(spinner, document.createTextNode("Updating delivery…"));
    select.disabled = true;
    select.setAttribute("aria-busy", "true");
    select.replaceWith(loading);

    var currentCart;
    loadCart()
      .then(function (cart) {
        currentCart = cart;
        return option.components.reduce(function (sequence, component) {
          return sequence.then(function () {
            var line = (currentCart.items || []).find(function (item) {
              var properties = item.properties || {};
              return (
                item.key === component.lineKey ||
                (properties._bb_bundle_key === bundleKey &&
                  properties._bb_bundle_role === component.role)
              );
            });
            if (
              !line ||
              !component.date ||
              !component.configuration
            ) {
              throw new Error("bundle_delivery_update_line_missing");
            }
            var properties = Object.assign({}, line.properties || {}, {
              "Delivery date": component.date,
              "_BadgeBuzz configuration": component.configuration,
            });
            var previousDate = String(
              (line.properties || {})["Delivery date"] || "",
            );
            delete properties["_BadgeBuzz offer"];
            if (
              automatic &&
              previousDate &&
              previousDate !== component.date
            ) {
              properties["_BadgeBuzz previous delivery date"] = previousDate;
            } else {
              delete properties["_BadgeBuzz previous delivery date"];
            }
            return changeLineAndReconcile(line, properties).then(function (result) {
              var replacement = result.line;
              updateLineKeyInDom(line.key, replacement.key);
              document
                .querySelectorAll(
                  '[data-bb-line-key="' +
                    escapeSelector(replacement.key) +
                    '"]',
                )
                .forEach(function (row) {
                  row.dataset.bbDeliveryDate = component.date;
                });
              currentCart = result.cart;
            });
          });
        }, Promise.resolve());
      })
      .then(function () {
        if (automatic) {
          state.automaticBundleDeliveryUpdates.set(bundleKey, {
            option: option,
            options: options,
          });
          paintAutomaticBundleDeliveryUpdate(bundleKey, option, options);
        } else {
          bundleHeadings(bundleKey).forEach(function (heading) {
            heading
              .querySelectorAll("[data-bb-delivery-notice]")
              .forEach(function (notice) {
                notice.remove();
              });
          });
        }
        state.deliveryRepairFailures.delete(repairKey);
        document.dispatchEvent(
          new CustomEvent("cart:updated", {
            detail: {source: "bundle-delivery-choice"},
          }),
        );
        return priceCart().catch(function () {
          schedulePricing();
        });
      })
      .catch(function () {
        if (automatic) state.deliveryRepairFailures.add(repairKey);
        if (loading.isConnected) loading.replaceWith(select);
        notify(
          "error",
          message("delivery_update_failed", "Ready-to-ship date was not changed"),
          message("reload_cart", "Reload the cart and try again."),
        );
      })
      .finally(function () {
        if (state.deliveryRepairing === repairKey) {
          state.deliveryRepairing = "";
        }
        select.disabled = false;
        select.removeAttribute("aria-busy");
      });
  }

  function paintBundleDeliveryChoice(error, allowAutomatic) {
    if (
      !error.bundleKey ||
      !Array.isArray(error.deliveryOptions) ||
      !error.deliveryOptions.length
    ) {
      return false;
    }
    var options = sortedDeliveryOptions(error.deliveryOptions);
    if (!options.length) return false;
    var repairKey = "bundle:" + error.bundleKey;
    var automatic =
      allowAutomatic !== false &&
      !state.deliveryRepairing &&
      !state.deliveryRepairFailures.has(repairKey);
    var automaticStarted = false;
    bundleHeadings(error.bundleKey).forEach(function (heading) {
      var details = heading.querySelector(".cart-item__details") || heading;
      var notice = details.querySelector("[data-bb-delivery-notice]");
      if (!notice) {
        notice = document.createElement("div");
        notice.dataset.bbDeliveryNotice = "";
        notice.className =
          "bb-cart-delivery-notice bb-cart-delivery-notice--error";
        notice.setAttribute("role", "alert");
        notice.setAttribute("aria-live", "polite");
        notice.innerHTML =
          '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="5.5" width="17" height="15" rx="2"></rect><path d="M8 3.5v4M16 3.5v4M3.5 10h17M8 15l2.3 2.3L16 12"></path></svg><span></span>';
        details.appendChild(notice);
      }
      notice.querySelector("span").textContent = error.message;
      notice
        .querySelectorAll(
          ".bb-cart-delivery-choice, .bb-cart-delivery-loading",
        )
        .forEach(function (existing) {
          existing.remove();
        });
      var select = document.createElement("select");
      select.className = "bb-cart-delivery-choice";
      select.setAttribute("aria-label", "Choose a new bundle ready-to-ship date");
      var placeholder = document.createElement("option");
      placeholder.value = "";
      placeholder.textContent = "Choose a new ready-to-ship date";
      placeholder.selected = true;
      placeholder.disabled = true;
      select.appendChild(placeholder);
      options.forEach(function (option, index) {
        var item = document.createElement("option");
        item.value = String(index);
        item.textContent =
          "Ready to ship by " +
          formatDeliveryDate(option.date) +
          " · " +
          money(option.total, option.currency);
        select.appendChild(item);
      });
      select.addEventListener("change", function () {
        chooseReplacementBundleDelivery(
          error.bundleKey,
          options[Number(select.value)],
          select,
          options,
          false,
        );
      });
      notice.appendChild(select);
      if (automatic && !automaticStarted) {
        automaticStarted = true;
        state.deliveryRepairing = repairKey;
        notice.className = "bb-cart-delivery-notice";
        notice.setAttribute("role", "status");
        notice.querySelector("span").textContent =
          "Updating to the earliest available bundle ready-to-ship date.";
        chooseReplacementBundleDelivery(
          error.bundleKey,
          options[0],
          select,
          options,
          true,
        );
      }
      var status = heading.querySelector("[data-bb-bundle-status]");
      if (status) {
        status.hidden = true;
        status.textContent = "";
      }
    });
    return true;
  }

  function paintDeliveryUpdates(lines) {
    if (!Array.isArray(lines)) return;
    lines.forEach(function (line) {
      var update = line.delivery_update;
      if (!update) return;
      paintDeliveryNotice(
        line.key,
        "(updated from " + formatDeliveryDate(update.previous_date) + ")",
        "info",
        update.date,
      );
    });
  }

  function paintBlockedPricing(data) {
    if (data && Array.isArray(data.lines)) {
      data.lines.forEach(paintLine);
    }
    document
      .querySelectorAll("[data-bb-authoritative-subtotal]")
      .forEach(function (target) {
        target.textContent = message(
          "pricing_unavailable",
          "Pricing unavailable",
        );
      });
    var errors = data && Array.isArray(data.errors) ? data.errors : [];
    errors.forEach(function (error) {
      if (
        error.code === "bundle_delivery_choice_required" &&
        paintBundleDeliveryChoice(error)
      ) {
        paintBundleBlocked(error.bundleKey);
        bundleHeadings(error.bundleKey).forEach(function (heading) {
          var total = heading.querySelector("[data-bb-bundle-total]");
          var unitPrice = heading.querySelector("[data-bb-bundle-unit-price]");
          var status = heading.querySelector("[data-bb-bundle-status]");
          if (total) total.textContent = "Choose a date";
          if (unitPrice) unitPrice.textContent = "";
          if (status) {
            status.hidden = true;
            status.textContent = "";
          }
        });
        return;
      }
      var bundleError = false;
      if (error.lineKey) {
        var selector =
          '[data-bb-line-key="' + escapeSelector(error.lineKey) + '"]';
        document.querySelectorAll(selector).forEach(function (row) {
          var bundleKey = row.dataset.bbBundleKey || "";
          if (bundleKey) {
            bundleError = true;
            row.querySelectorAll(".bb-item-price").forEach(function (target) {
              target.hidden = true;
            });
            row
              .querySelectorAll(
                ".bb-cart__item-price, [data-bb-item-total], .cart-item__price-wrapper .price--end",
              )
              .forEach(function (target) {
                target.textContent = "Price unavailable";
              });
            paintBundleBlocked(bundleKey);
          } else {
            row
              .querySelectorAll(
                ".bb-cart__item-price, .bb-item-price, [data-bb-item-total], .cart-item__price-wrapper .price--end",
              )
              .forEach(function (target) {
                target.textContent = message(
                  "pricing_unavailable",
                  "Pricing unavailable",
                );
              });
          }
          delete row.dataset.bbAuthoritativePrice;
        });
      }
      if (!paintDeliveryChoice(error) && !bundleError) {
        paintDeliveryNotice(
          error.lineKey,
          error.message ||
            message("unavailable_detail", "Review the cart and try again."),
          "error",
        );
      }
    });
    document.documentElement.dataset.bbCheckoutMode = "blocked";
    setCheckoutBlocked(true);
  }

  function paintPricing(data) {
    if (!data) return;
    if (data.mode === "blocked") {
      paintBlockedPricing(data);
      return;
    }
    document
      .querySelectorAll(".bb-cart-delivery-notice--error")
      .forEach(function (notice) {
        notice.remove();
      });
    if (!Array.isArray(data.lines)) return;
    data.lines.forEach(paintLine);
    paintBundlePricing(data.lines);
    if (data.subtotal != null) {
      var formatted = money(data.subtotal, data.currency);
      document
        .querySelectorAll("[data-bb-authoritative-subtotal]")
        .forEach(function (target) {
          target.textContent = formatted;
        });
    }
    document.documentElement.dataset.bbCheckoutMode = data.mode || "";
    setCheckoutBlocked(false);
  }

  function firstError(data) {
    return (
      data && Array.isArray(data.errors) && data.errors.length && data.errors[0]
    );
  }

  function deliveryUpdates(data) {
    if (!data || !Array.isArray(data.lines)) return [];
    return data.lines.filter(function (line) {
      return line.delivery_update;
    });
  }

  function updateLineKeyInDom(previousKey, nextKey) {
    if (!previousKey || !nextKey || previousKey === nextKey) return;
    // Property changes also change Shopify's key used by upload and bundle controls.
    ['data-bb-line-key', 'data-item-key', 'data-bb-cart-component-key'].forEach(function (attribute) {
      var selector = '[' + attribute + '="' + escapeSelector(previousKey) + '"]';
      document.querySelectorAll(selector).forEach(function (element) {
        element.setAttribute(attribute, nextKey);
      });
    });
  }

  function normalizedProperties(properties) {
    var normalized = {};
    Object.keys(properties || {})
      .sort()
      .forEach(function (key) {
        var value = properties[key];
        if (value == null || String(value) === "") return;
        normalized[key] = String(value);
      });
    return normalized;
  }

  function sameProperties(actual, expected) {
    return (
      JSON.stringify(normalizedProperties(actual)) ===
      JSON.stringify(normalizedProperties(expected))
    );
  }

  function findExactChangedLine(cart, originalLine, properties) {
    if (!cart || !Array.isArray(cart.items) || !originalLine) return null;
    return cart.items.find(function (item) {
      return (
        String(item.variant_id) === String(originalLine.variant_id) &&
        Number(item.quantity) === Number(originalLine.quantity) &&
        sameProperties(item.properties, properties)
      );
    });
  }

  function changeLineAndReconcile(originalLine, properties) {
    var mutationError = null;
    return post("/cart/change.js", {
      id: originalLine.key,
      quantity: originalLine.quantity,
      properties: properties,
    })
      .then(function (result) {
        var replacement = result.response.ok
          ? findExactChangedLine(result.data, originalLine, properties)
          : null;
        if (replacement) return { cart: result.data, line: replacement };
        mutationError = new Error(
          (result.data && (result.data.message || result.data.description)) ||
            "delivery_update_failed",
        );
        return loadCart().then(function (cart) {
          var reconciled = findExactChangedLine(cart, originalLine, properties);
          if (!reconciled) throw mutationError;
          return { cart: cart, line: reconciled };
        });
      })
      .catch(function (error) {
        mutationError = mutationError || error;
        return loadCart().then(function (cart) {
          var reconciled = findExactChangedLine(cart, originalLine, properties);
          if (!reconciled) throw mutationError;
          return { cart: cart, line: reconciled };
        });
      });
  }

  function applyDeliveryUpdates(cart, pricingData) {
    var updates = deliveryUpdates(pricingData);
    var currentCart = cart;
    return updates
      .reduce(function (sequence, pricedLine) {
        return sequence.then(function () {
          var cartLine = (currentCart.items || []).find(function (item) {
            return item.key === pricedLine.key;
          });
          var update = pricedLine.delivery_update;
          if (!cartLine || !update) {
            throw new Error("delivery_update_line_missing");
          }
          var properties = Object.assign({}, cartLine.properties || {}, {
            "Delivery date": update.date,
            "_BadgeBuzz configuration": update.configuration,
            "_BadgeBuzz previous delivery date": update.previous_date,
          });
          if (update.offer) {
            properties["_BadgeBuzz offer"] = update.offer;
          } else {
            delete properties["_BadgeBuzz offer"];
          }
          return changeLineAndReconcile(cartLine, properties).then(function (result) {
            updateLineKeyInDom(cartLine.key, result.line.key);
            currentCart = result.cart;
          });
        });
      }, Promise.resolve())
      .then(function () {
        return currentCart;
      });
  }

  function requestPricing(cart) {
    return post(PRICE_ENDPOINT, { lines: cart.items });
  }

  function localBlockedResult(cart, lineKey, shouldPaint) {
    var data = {
      mode: "blocked",
      pricing_fingerprint: "",
      currency: "USD",
      lines: [],
      errors: [
        {
          code: "delivery_update_failed",
          lineKey: lineKey || "",
          message:
            "Delivery could not be refreshed. Reload the cart and try again.",
        },
      ],
    };
    if (shouldPaint !== false) paintBlockedPricing(data);
    return {
      cart: cart,
      data: data,
      response: { ok: false, status: 422 },
    };
  }

  function pricingResult(revision, cart, data, response) {
    return {
      cart: cart,
      data: data,
      response: response,
      revision: revision,
      stale: revision !== state.cartRevision,
    };
  }

  function priceCart(options) {
    options = options || {};
    var revision = state.cartRevision;
    if (
      !options.fresh &&
      state.pricingPromise &&
      state.pricingRevision === revision
    ) {
      return state.pricingPromise;
    }
    var promise = loadCart()
      .then(function (cart) {
        if (!cart.items || !cart.items.length) {
          if (revision === state.cartRevision) setCheckoutBlocked(false);
          return pricingResult(revision, cart, null, { ok: true });
        }
        return requestPricing(cart).then(function (result) {
          if (revision !== state.cartRevision) {
            return pricingResult(revision, cart, result.data, result.response);
          }
          if (!result.response.ok || result.data.mode === "blocked") {
            paintBlockedPricing(result.data);
            return pricingResult(revision, cart, result.data, result.response);
          }
          var updates = deliveryUpdates(result.data);
          if (!updates.length) {
            paintPricing(result.data);
            return pricingResult(revision, cart, result.data, result.response);
          }
          result.data.lines.forEach(paintLine);
          paintDeliveryUpdates(result.data.lines);
          return applyDeliveryUpdates(cart, result.data)
            .then(function (updatedCart) {
              return requestPricing(updatedCart).then(function (stable) {
                if (revision === state.cartRevision) {
                  if (!stable.response.ok || stable.data.mode === "blocked") {
                    paintBlockedPricing(stable.data);
                  } else {
                    paintPricing(stable.data);
                  }
                }
                return pricingResult(
                  revision,
                  updatedCart,
                  stable.data,
                  stable.response,
                );
              });
            })
            .catch(function () {
              var blocked = localBlockedResult(
                cart,
                updates[0].key,
                revision === state.cartRevision,
              );
              return pricingResult(
                revision,
                blocked.cart,
                blocked.data,
                blocked.response,
              );
            });
        });
      })
      .finally(function () {
        if (state.pricingPromise === promise) {
          state.pricingPromise = null;
          state.pricingRevision = -1;
        }
      });
    state.pricingPromise = promise;
    state.pricingRevision = revision;
    return promise;
  }

  function priceStableCart(attempt) {
    return priceCart({ fresh: true }).then(function (result) {
      if (!result.stale) return result;
      if ((attempt || 0) >= 2) throw new Error("cart_changed_during_pricing");
      return priceStableCart((attempt || 0) + 1);
    });
  }

  function paintPricingFailure() {
    document
      .querySelectorAll(
        "[data-bb-authoritative-subtotal], .bb-item-price, [data-bb-item-total], [data-bb-bundle-total], [data-bb-bundle-unit-price]",
      )
      .forEach(function (target) {
        var row = target.closest && target.closest("[data-bb-line-key]");
        var isAuthoritativeTarget =
          target.matches(
            "[data-bb-authoritative-subtotal], [data-bb-bundle-total], [data-bb-bundle-unit-price]",
          ) ||
          /Checking price/i.test(target.textContent || "") ||
          (row && row.dataset.bbAuthoritativePrice);
        if (!isAuthoritativeTarget) return;
        target.hidden = false;
        target.textContent = message("pricing_unavailable", "Pricing unavailable");
      });
    document.documentElement.dataset.bbCheckoutMode = "unavailable";
    state.pricingFingerprint = "";
    setCheckoutBlocked(false);
  }

  function showPricingError(result) {
    var error = firstError(result.data);
    notify(
      "error",
      message("unavailable", "Checkout is unavailable"),
      (error && error.message) ||
        (result.data && result.data.message) ||
        message("unavailable_detail", "Review the cart and try again."),
    );
  }

  function currentIdempotencyKey(fingerprint) {
    if (state.idempotencyKey && state.idempotencyFingerprint === fingerprint) {
      return state.idempotencyKey;
    }
    try {
      var stored = JSON.parse(
        window.sessionStorage.getItem(IDEMPOTENCY_STORAGE_KEY) || "null",
      );
      if (
        stored &&
        stored.fingerprint === fingerprint &&
        typeof stored.key === "string" &&
        stored.key.length >= 8
      ) {
        state.idempotencyKey = stored.key;
        state.idempotencyFingerprint = stored.fingerprint;
        return state.idempotencyKey;
      }
    } catch (error) {
      // In-memory idempotency still protects the current page when storage is unavailable.
    }
    if (!state.idempotencyKey || state.idempotencyFingerprint !== fingerprint) {
      state.idempotencyKey =
        window.crypto && typeof window.crypto.randomUUID === "function"
          ? window.crypto.randomUUID()
          : [Date.now().toString(36), Math.random().toString(36).slice(2)].join(
              "-",
            );
      state.idempotencyFingerprint = fingerprint;
    }
    try {
      window.sessionStorage.setItem(
        IDEMPOTENCY_STORAGE_KEY,
        JSON.stringify({
          fingerprint: state.idempotencyFingerprint,
          key: state.idempotencyKey,
        }),
      );
    } catch (error) {
      // Storage can be disabled; the current-page key remains stable in memory.
    }
    return state.idempotencyKey;
  }

  function clearIdempotencyKey() {
    state.idempotencyKey = "";
    state.idempotencyFingerprint = "";
    try {
      window.sessionStorage.removeItem(IDEMPOTENCY_STORAGE_KEY);
    } catch (error) {
      // Nothing else is required when storage is unavailable.
    }
  }

  function definitivePrepareError(code) {
    return [
      "pricing_changed",
      "idempotency_conflict",
      "checkout_session_forbidden",
      "checkout_session_expired",
      "checkout_already_completed",
    ].includes(code);
  }

  function redirectTo(url) {
    state.redirecting = true;
    try {
      window.location.assign(url);
    } catch (error) {
      state.redirecting = false;
      throw error;
    }
  }

  function resumeNativeCheckout(button) {
    state.nativeBypass = true;
    state.redirecting = true;
    if (button.form && typeof button.form.requestSubmit === "function") {
      try {
        // Disabled submitters are omitted from form data, which turns Shopify's
        // native checkout submission into an ordinary POST back to /cart.
        button.disabled = false;
        button.form.requestSubmit(button);
      } catch (error) {
        state.redirecting = false;
        throw error;
      }
      return;
    }
    redirectTo("/checkout");
  }

  function prepareCheckout(button) {
    var displayedFingerprint = state.pricingFingerprint;
    state.redirecting = false;
    setBusy(true);
    return priceStableCart()
      .then(function (priced) {
        if (!priced.cart.items || !priced.cart.items.length) {
          redirectTo("/cart");
          return null;
        }
        if (!priced.response.ok || priced.data.mode === "blocked") {
          showPricingError(priced);
          return null;
        }
        var currentFingerprint = priced.data.pricing_fingerprint || "";
        if (
          displayedFingerprint &&
          currentFingerprint &&
          displayedFingerprint !== currentFingerprint
        ) {
          state.pricingFingerprint = currentFingerprint;
          notify(
            "warning",
            message("pricing_changed", "Pricing updated"),
            message(
              "pricing_changed_detail",
              "Review the new total, then select Checkout again.",
            ),
          );
          return null;
        }
        state.pricingFingerprint = currentFingerprint;
        if (priced.data.mode === "native") {
          resumeNativeCheckout(button);
          return null;
        }
        return post(PREPARE_ENDPOINT, {
          lines: priced.cart.items,
          pricing_fingerprint: currentFingerprint,
          idempotency_key: currentIdempotencyKey(currentFingerprint),
        });
      })
      .then(function (prepared) {
        if (!prepared) return;
        paintPricing(prepared.data);
        var prepareError = prepared.data && prepared.data.error;
        if (prepareError === "checkout_preparing") {
          notify(
            "warning",
            message("preparing", "Your checkout is still being prepared"),
            message(
              "preparing_detail",
              "Please select Checkout again shortly.",
            ),
          );
          return;
        }
        if (prepareError === "pricing_changed") {
          if (prepared.data.pricing_fingerprint) {
            state.pricingFingerprint = prepared.data.pricing_fingerprint;
          }
          notify(
            "warning",
            message("pricing_changed", "Pricing updated"),
            prepared.data.message ||
              message(
                "pricing_changed_detail",
                "Review the new total, then select Checkout again.",
              ),
          );
          clearIdempotencyKey();
          return;
        }
        if (definitivePrepareError(prepareError)) clearIdempotencyKey();
        if (!prepared.response.ok || !prepared.data.checkout_url) {
          showPricingError(prepared);
          return;
        }
        redirectTo(prepared.data.checkout_url);
      })
      .catch(function () {
        paintPricingFailure();
        notify(
          "error",
          message("unavailable", "Checkout is unavailable"),
          message("network_error", "We could not verify the cart. Try again."),
        );
      })
      .finally(function () {
        if (!state.redirecting) setBusy(false);
      });
  }

  function clickedCheckout(event) {
    var button = event.target.closest('[name="checkout"]');
    if (!button || state.nativeBypass) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    prepareCheckout(button);
  }

  function submittedCheckout(event) {
    var submitter = event.submitter;
    if (!submitter || submitter.name !== "checkout" || state.nativeBypass) {
      if (state.nativeBypass) state.nativeBypass = false;
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    prepareCheckout(submitter);
  }

  function schedulePricing() {
    window.clearTimeout(state.timer);
    state.timer = window.setTimeout(function () {
      if (!checkoutButtons().length) return;
      var revision = state.cartRevision;
      priceCart()
        .then(function (result) {
          if (result.stale || result.revision !== state.cartRevision) return;
          if (
            result.response.ok &&
            result.data &&
            result.data.pricing_fingerprint
          ) {
            state.pricingFingerprint = result.data.pricing_fingerprint;
          } else if (!result.response.ok || result.data?.mode === "blocked") {
            state.pricingFingerprint = "";
          }
        })
        .catch(function () {
          if (revision === state.cartRevision) paintPricingFailure();
        });
    }, 120);
  }

  function cartChanged() {
    state.cartRevision += 1;
    schedulePricing();
  }

  function initialize() {
    document.addEventListener("click", clickedCheckout, true);
    document.addEventListener("submit", submittedCheckout, true);
    document.addEventListener("cart:updated", cartChanged);
    document.addEventListener("change", function (event) {
      if (event.target.matches('input[name="updates[]"]')) {
        cartChanged();
      }
    });
    document.addEventListener("click", function (event) {
      if (event.target.closest("[data-bb-cart-trigger]")) {
        schedulePricing();
      }
    });
    if (
      typeof window.subscribe === "function" &&
      window.PUB_SUB_EVENTS &&
      window.PUB_SUB_EVENTS.cartUpdate
    ) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, cartChanged);
    }
    schedulePricing();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }
})();
